import {
  ListBrowseToolbar,
  FilterValuePill,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  PageHeaderActionPrimary,
  PageHeaderActions,
  Textarea,
  CURRENT_CLIENT,
  getPageHeader,
  formatBytes,
} from '@yeshuman/sdk'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { HealthListPageSkeleton } from './health-list-page-skeleton'
import { FileText, Loader2, Plus, Upload } from 'lucide-react'
import { toast } from 'sonner'
import {
  HealthRecordFilters,
  HealthRecordItem,
  HealthRecordType,
  uploadHealthRecordFile,
  useCreateHealthRecordIngestion,
  useHealthRecords,
} from '../hooks/use-health-records'
import { useHealthRecordSearch } from '../hooks/use-health-record-search'
import {
  formatDateTime,
  formatPercent,
  formatRecordDate,
  SOURCE_LABELS,
  StatusBadge,
  TYPE_LABELS,
} from './health-record-display'

const ACCEPTED_RECORD_FILES = [
  'application/pdf',
  'image/*',
  'text/*',
  '.csv',
  '.doc',
  '.docx',
  '.rtf',
  '.xls',
  '.xlsx',
  '.heic',
  '.dcm',
].join(',')

/** Stable ordering for dynamic type pills (subset shown depends on user's records). */
const HEALTH_RECORD_TYPE_ORDER: HealthRecordType[] = [
  'test_result',
  'imaging',
  'referral',
  'prescription',
  'general_document',
  'form',
  'correspondence',
  'receipt_or_invoice',
  'unknown',
]

type WorkflowFilterKey = 'all' | 'processing' | 'needs_review'

type RecordTypeFilterKey = `record_type:${HealthRecordType}`

type FilterKey = WorkflowFilterKey | RecordTypeFilterKey

function recordTypeFilterKey(recordType: HealthRecordType): RecordTypeFilterKey {
  return `record_type:${recordType}`
}

function filterRecords(records: HealthRecordItem[], filter: FilterKey) {
  if (filter === 'processing') {
    return records.filter((record) =>
      record.classification_status === 'pending' || record.classification_status === 'processing',
    )
  }
  if (filter === 'needs_review') {
    return records.filter((record) => ['needs_review', 'unsupported', 'failed'].includes(record.classification_status))
  }
  if (filter.startsWith('record_type:')) {
    const recordType = filter.slice('record_type:'.length) as HealthRecordType
    return records.filter((record) => record.record_type === recordType)
  }
  return records
}

function distinctRecordTypesPresent(records: HealthRecordItem[]): HealthRecordType[] {
  const seen = new Set<HealthRecordType>()
  for (const r of records) seen.add(r.record_type)
  return HEALTH_RECORD_TYPE_ORDER.filter((t) => seen.has(t))
}

function recordHint(record: HealthRecordItem) {
  if (record.classification_status === 'pending' || record.classification_status === 'processing') {
    return 'Lumie is filing this record.'
  }
  if (record.classification_status === 'needs_review') {
    return 'Needs review before this record is fully filed.'
  }
  if (record.attachments.length > 0) return 'Files attached and ready for review.'
  return 'No files attached.'
}

export function RecordsPage() {
  const [activeFilter, setActiveFilter] = useState<FilterKey>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [isUploadOpen, setIsUploadOpen] = useState(false)
  const [userHint, setUserHint] = useState('')
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const healthRecords = (CURRENT_CLIENT as any).healthRecords
  const description =
    healthRecords?.description ||
    'Access and manage medical records, test results, referrals, scans, forms, and other health files.'

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300)
    return () => window.clearTimeout(timer)
  }, [searchTerm])

  const apiFilters = useMemo((): HealthRecordFilters => {
    const filters: HealthRecordFilters = { limit: 100 }
    if (activeFilter.startsWith('record_type:')) {
      filters.record_type = activeFilter.slice('record_type:'.length) as HealthRecordType
    }
    return filters
  }, [activeFilter])

  const isSearchActive = debouncedSearchTerm.trim().length > 0
  const searchFilters = useMemo(
    () => ({
      limit: 100,
      ...(apiFilters.record_type ? { record_type: apiFilters.record_type } : {}),
    }),
    [apiFilters.record_type],
  )

  const {
    records: listedRecords,
    isLoading: isListLoadingBase,
    error: listError,
    refetch,
  } = useHealthRecords(apiFilters, !isSearchActive)
  const {
    results: searchResults,
    isLoading: isSearchLoading,
    error: searchError,
  } = useHealthRecordSearch(debouncedSearchTerm, isSearchActive, searchFilters)

  const records = isSearchActive ? searchResults : listedRecords
  const isLoading = isSearchActive ? isSearchLoading : isListLoadingBase
  const error = isSearchActive ? searchError : listError
  const isSearchPending = searchTerm !== debouncedSearchTerm && searchTerm.trim().length > 0
  const isListLoading = isLoading || isSearchPending
  const createIngestion = useCreateHealthRecordIngestion()

  const recordTypesPresent = useMemo(() => distinctRecordTypesPresent(records), [records])

  useEffect(() => {
    if (activeFilter.startsWith('record_type:')) {
      const recordType = activeFilter.slice('record_type:'.length) as HealthRecordType
      if (!records.some((r) => r.record_type === recordType)) {
        setActiveFilter('all')
      }
    } else if (activeFilter === 'needs_review') {
      const hasAny = records.some((r) =>
        ['needs_review', 'unsupported', 'failed'].includes(r.classification_status),
      )
      if (!hasAny) setActiveFilter('all')
    } else if (activeFilter === 'processing') {
      const hasAny = records.some(
        (r) => r.classification_status === 'pending' || r.classification_status === 'processing',
      )
      if (!hasAny) setActiveFilter('all')
    }
  }, [records, activeFilter])

  const visibleRecords = useMemo(() => {
    if (activeFilter.startsWith('record_type:')) return records
    return filterRecords(records, activeFilter)
  }, [records, activeFilter])

  const hasActiveSearchOrFilter =
    debouncedSearchTerm.trim().length > 0 || activeFilter !== 'all'
  const isEmptyLibrary = records.length === 0 && !hasActiveSearchOrFilter
  const selectedFileSummary = useMemo(
    () => selectedFiles.map((file) => `${file.name} (${formatBytes(file.size)})`).join(', '),
    [selectedFiles],
  )

  const filterCounts = useMemo(() => {
    const counts: Partial<Record<FilterKey, number>> & Pick<Record<WorkflowFilterKey, number>, 'all'> = {
      all: records.length,
      processing: filterRecords(records, 'processing').length,
      needs_review: filterRecords(records, 'needs_review').length,
    }
    for (const recordType of recordTypesPresent) {
      const key = recordTypeFilterKey(recordType)
      counts[key] = records.filter((r) => r.record_type === recordType).length
    }
    return counts as Record<FilterKey, number>
  }, [records, recordTypesPresent])

  const workflowPills: { key: WorkflowFilterKey; label: string }[] = [
    ['all', 'All'],
    ['processing', 'Processing'],
    ['needs_review', 'Needs review'],
  ].map(([key, label]) => ({ key: key as WorkflowFilterKey, label }))

  const visibleWorkflowPills = workflowPills.filter(({ key }) => {
    if (key === 'processing') return filterCounts.processing > 0
    if (key === 'needs_review') return filterCounts.needs_review > 0
    return true
  })

  const resetUpload = () => {
    setUserHint('')
    setSelectedFiles([])
    if (inputRef.current) inputRef.current.value = ''
  }

  const handleCreate = async () => {
    if (selectedFiles.length === 0) {
      toast.error('Choose at least one file')
      return
    }

    setIsUploading(true)
    try {
      const uploaded = []
      for (const file of selectedFiles) {
        uploaded.push(await uploadHealthRecordFile(file))
      }

      await createIngestion.mutateAsync({
        file_asset_ids: uploaded.map((file) => String(file.id)),
        user_hint: userHint,
        source: 'records_upload',
      })

      toast.success('Files added for Lumie to file')
      setIsUploadOpen(false)
      resetUpload()
      await refetch()
      window.setTimeout(() => void refetch(), 2500)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add files'
      toast.error(message)
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <PageContainer>
        <PageHeader
          leading={<PageHeaderLeading />}
          {...getPageHeader('/records', 'Records', description)}
          icon={<FileText className="h-5 w-5" />}
          actions={
            <PageHeaderActions
              primary={
                <PageHeaderActionPrimary onClick={() => setIsUploadOpen(true)}>
                  <Plus className="h-4 w-4" aria-hidden />
                  Add files
                </PageHeaderActionPrimary>
              }
            />
          }
        />

        <Card>
          <CardHeader className="gap-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <CardTitle>Health records</CardTitle>
                <CardDescription>
                  Upload files once. Lumie will classify, group, and summarise them where possible.
                </CardDescription>
              </div>
              <Button variant="outline" onClick={() => setIsUploadOpen(true)}>
                <Upload className="mr-2 h-4 w-4" />
                Upload files
              </Button>
            </div>
            <ListBrowseToolbar
              searchId="health-records-search"
              searchPlaceholder="Search records, file names, or document text…"
              searchValue={searchTerm}
              onSearchChange={setSearchTerm}
            />
            <div className="flex flex-wrap gap-2">
              {visibleWorkflowPills.map(({ key, label }) => (
                <FilterValuePill
                  key={key}
                  label={label}
                  count={filterCounts[key] || undefined}
                  selected={activeFilter === key}
                  onSelect={() => setActiveFilter(key)}
                />
              ))}
              {recordTypesPresent.map((recordType) => {
                const key = recordTypeFilterKey(recordType)
                return (
                  <FilterValuePill
                    key={key}
                    label={TYPE_LABELS[recordType]}
                    count={filterCounts[key] || undefined}
                    selected={activeFilter === key}
                    onSelect={() => setActiveFilter(key)}
                  />
                )
              })}
            </div>
          </CardHeader>
          <CardContent>
            {error ? (
              <div className="rounded-lg border border-destructive p-4">
                <p className="text-sm text-destructive">Failed to load health records.</p>
                <Button variant="outline" size="sm" onClick={() => void refetch()}>
                  Retry
                </Button>
              </div>
            ) : isListLoading ? (
              <HealthListPageSkeleton showFilters={false} count={3} ariaLabel="Loading health records" />
            ) : visibleRecords.length === 0 ? (
              <div className="rounded-lg border border-dashed p-8 text-center">
                <FileText className="mx-auto mb-4 h-10 w-10 text-muted-foreground" />
                {isEmptyLibrary ? (
                  <>
                    <h3 className="font-medium">No records yet</h3>
                    <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
                      Add PDFs, scans, images, forms, or documents and Lumie will file them into this library.
                    </p>
                    <Button onClick={() => setIsUploadOpen(true)}>
                      <Plus className="mr-2 h-4 w-4" />
                      Add files
                    </Button>
                  </>
                ) : (
                  <>
                    <h3 className="font-medium">No matching records</h3>
                    <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
                      {debouncedSearchTerm.trim()
                        ? `No records match "${debouncedSearchTerm.trim()}". Try different words or clear your search.`
                        : 'No records match the current filters. Try another filter or clear your selection.'}
                    </p>
                    <div className="mt-4 flex flex-wrap justify-center gap-2">
                      {debouncedSearchTerm.trim() ? (
                        <Button variant="outline" onClick={() => setSearchTerm('')}>
                          Clear search
                        </Button>
                      ) : null}
                      {activeFilter !== 'all' ? (
                        <Button variant="outline" onClick={() => setActiveFilter('all')}>
                          Show all records
                        </Button>
                      ) : null}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="grid gap-4">
                {visibleRecords.map((record) => (
                  <Link
                    key={record.id}
                    to={`/records/${record.id}`}
                    className="block rounded-lg border p-4 transition-colors hover:bg-muted/50"
                  >
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                      <div className="min-w-0 space-y-3">
                        <div className="space-y-2 min-w-0">
                          <h3
                            className="min-w-0 font-medium break-words [overflow-wrap:anywhere]"
                            title={record.title}
                          >
                            {record.title}
                          </h3>
                          <div className="flex flex-wrap items-center gap-2">
                            <Badge variant="secondary">{TYPE_LABELS[record.record_type]}</Badge>
                            <StatusBadge status={record.classification_status} />
                            <Badge variant="outline">{record.attachment_count} files</Badge>
                          </div>
                        </div>
                        <p className="line-clamp-3 text-sm text-muted-foreground">
                          {record.summary ||
                            record.description ||
                            'No summary yet. Ask Lumie to review and summarise this record.'}
                        </p>
                        <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-muted-foreground">
                          <span>{recordHint(record)}</span>
                          <span>Confidence {formatPercent(record.classification_confidence)}</span>
                          <span>Source {SOURCE_LABELS[record.source] || record.source}</span>
                        </div>
                      </div>
                      <div className="flex shrink-0 flex-col gap-1 text-xs text-muted-foreground lg:text-right">
                        <span>{formatRecordDate(record.record_date)}</span>
                        <span>Updated {formatDateTime(record.updated_at)}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

      <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>Add health files</DialogTitle>
            <DialogDescription>
              Choose one or more files. Add a hint if they belong together, such as "these photos are one referral."
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Files</Label>
              <input
                ref={inputRef}
                type="file"
                multiple
                accept={ACCEPTED_RECORD_FILES}
                className="hidden"
                onChange={(event) => setSelectedFiles(Array.from(event.target.files ?? []))}
              />
              <Button type="button" variant="outline" onClick={() => inputRef.current?.click()}>
                <Upload className="mr-2 h-4 w-4" />
                Choose files
              </Button>
              {selectedFiles.length > 0 ? (
                <p className="text-sm text-muted-foreground">{selectedFileSummary}</p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  PDFs, images, text, Word, spreadsheet, and some medical file types can be stored. Some types may
                  need review before Lumie can parse them.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="record-user-hint">Hint for Lumie</Label>
              <Textarea
                id="record-user-hint"
                value={userHint}
                onChange={(event) => setUserHint(event.target.value)}
                placeholder="e.g. These three photos are one specialist referral"
                className="placeholder:italic"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsUploadOpen(false)} disabled={isUploading}>
              Cancel
            </Button>
            <Button onClick={() => void handleCreate()} disabled={isUploading || createIngestion.isPending}>
              {isUploading || createIngestion.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              File with Lumie
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageContainer>
  )
}
